<template>
  <div class="border-top p-2 d-flex align-items-end rs-bg-content">
    <div class="mr-2">
      <label>Width</label>
      <BInputGroup size="sm">
        <BFormInput v-model="width" type="number" @input="handleWidthChange" />

        <BInputGroupAppend>
          <BButton
            title="Image width in pixels"
            :variant="imageWidthUoM === 'px' ? 'secondary' : 'outline-secondary'"
            @click="handleWidthUoMChange('px')"
          >
            px
          </BButton>
          <BButton
            title="Image width in percentage"
            :variant="imageWidthUoM === '%' ? 'secondary' : 'outline-secondary'"
            @click="handleWidthUoMChange('%')"
          >
            &percnt;
          </BButton>
        </BInputGroupAppend>
      </BInputGroup>
    </div>

    <div class="mr-2">
      <label>Alt Text</label>
      <BFormInput
        v-model="alt"
        size="sm"
        :debounce="400"
      />
    </div>

    <div>
      <label>Align</label>
      <BFormSelect
        v-model="align"
        size="sm"
        :options="alignOptions"
        @input="handleChange('align', $event)"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { BFormSelect } from 'bootstrap-vue';
import { Editor } from '@tiptap/vue-2';
import { SetImageOptions } from '@tiptap/extension-image';
import { alignOptions } from '@/shared/tiptap/consts';
import { convertStyleObjectToString, convertStyleStringToObject } from '@/shared/tiptap/helpers';
import { TEditableImage } from '@/shared/tiptap/types';

export default defineComponent({
  props: {
    /**
     * The editor instance shared by toolbar and form parent components
     */
    editor: {
      type: Object as PropType<Editor>,
      required: true,
    },
  },

  data() {
    return {
      alignOptions,
      width: null as number | null,
      imageWidthUoM: '%',
      align: '',
      alt: '',
    };
  },

  watch: {
    alt(newVal) {
      this.handleChange('alt', newVal);
    },

    'editor.state.selection': {
      handler() {
        if (this.editor.isFocused && this.editor.isActive('image')) {
          this.setInitialState();
        }
      },
    },
  },

  mounted() {
    this.setInitialState();
  },

  methods: {
    getStyles() {
      const attrs = this.editor.getAttributes('image');
      return convertStyleStringToObject(attrs.style ?? '');
    },

    setInitialState() {
      const attrs = this.editor.getAttributes('image');
      const styles = this.getStyles();

      this.width = Number(styles.width?.replace(/%|px/, ''));
      this.imageWidthUoM = styles.width?.includes('%') ? '%' : 'px';
      this.align = attrs.align;
      this.alt = attrs.alt;
    },

    /**
     * When attribute `key`'s `value` has changed, update it on the editor and emit it via `@input`
     * @param key
     * @param content
     */
    handleChange(key: keyof TEditableImage, content: string) {
      const attrs = this.editor.getAttributes('image') as SetImageOptions;
      const newValue = { ...attrs, [key]: content };

      this.editor
        .chain()
        .updateAttributes('image', newValue)
        .run();
    },

    /**
     * When width changes, converts the styles computed object and stitches in the new width, calls `handleChange`
     */
    handleWidthChange(newVal: string) {
      const styles = this.getStyles();

      this.handleChange(
        'style',
        convertStyleObjectToString({ ...styles, width: `${newVal}${this.imageWidthUoM}` }),
      );
    },

    /**
     * Side effects to set width state if width's unit of measure changes (options are `'px' | '%'`)
     */
    handleWidthUoMChange(newVal: '%' | 'px') {
      this.imageWidthUoM = newVal;

      // If width is greater than 100px currently, set it to 100%.
      if (newVal === '%' && (this.width ?? 100) > 100) {
        this.handleWidthChange('100');
        return;
      }

      this.handleWidthChange(`${this.width}`);
    },
  },
});
</script>

<style lang="scss">
@import "@/styles/_variables.scss";
</style>
